import { useEffect, useCallback, useRef } from 'react';
import cx from 'classnames';

import { Backdrop, ClickAwayListener, Paper, Popper, Collapse, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';

import Button from '@classify/react-core-components/Button';
import Typography from '@classify/react-core-components/Typography';
import { alpha } from '@classify/react-core-components/styles';

import browser from '@openclassrooms/utils/browser';
import useDynamicSize from '@openclassrooms/ui-web/utils/hooks/useDynamicSize';

const useStyles = makeStyles(({ zIndex, palette, spacing, typography, transitions }) => ({
  backdrop: {
    backgroundColor: alpha(palette.background.secondary, 0.32),
  },
  popper: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: zIndex.modal,
  },
  dropdownRoot: {
    width: '100%',
    height: '100%',
    backgroundColor: palette.background.primary,
  },
  button: {
    color: palette.text.onMain,
    fontWeight: typography.fontWeightRegular,
    textTransform: 'none',
    '&:focus': {
      outline: `${palette.brand.main} auto 1px`,
    },
    '&:hover, &:active': {
      color: palette.brand.main,
      backgroundColor: 'transparent',
      outline: '0',
    },
  },
  isActive: {
    color: palette.brand.main,
    '&:focus': {
      outline: '0',
    },

    '& $buttonIcon': {
      transform: 'rotate(-180deg)',
    },
  },
  buttonIcon: {
    transition: transitions.create('transform', {
      duration: transitions.duration.shortest,
    }),
    verticalAlign: 'middle',
    margin: spacing(0, 0, 0, 0.5),
    pointerEvents: 'none',
    position: 'relative',
  },
}));

const HeaderNavigationDropDown = ({
  isOpen,
  link,
  anchorEl = null,
  toggleMenu,
  name,
  menuComponent: MenuComponent,
}) => {
  const classes = useStyles();
  const { refHeight } = useDynamicSize(anchorEl.current);
  const menuParentRef = useRef(null);
  const { content, dataEvent } = link;
  const { current: parentRef } = anchorEl;
  const hasReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const transitionDuration = hasReducedMotion ? 0 : 200;

  const closeMenu = useCallback(() => toggleMenu(false), [toggleMenu]);

  const handleToggleMenu = (event) => {
    toggleMenu(!isOpen);
    event.currentTarget.blur();
  };

  const handleUserKeyPress = useCallback(
    (event) => {
      const { key, target } = event;
      const { current: menuParent } = menuParentRef;

      if (key === 'Tab' && !menuParent.contains(target)) {
        closeMenu();
      }
    },
    [closeMenu]
  );

  useEffect(() => {
    if (isOpen) {
      browser.window.addEventListener('scroll', closeMenu);
      browser.window.addEventListener('keyup', handleUserKeyPress);
    }

    return () => {
      browser.window.removeEventListener('scroll', closeMenu);
      browser.window.removeEventListener('keyup', handleUserKeyPress);
    };
  }, [closeMenu, handleUserKeyPress, isOpen, parentRef]);

  return (
    <>
      <ClickAwayListener
        onClickAway={closeMenu}
        mouseEvent={isOpen ? 'onClick' : false}
        touchEvent={isOpen ? 'onTouchEnd' : false}
      >
        <div>
          <Button
            variant="tertiary"
            aria-controls={`header-submenu-${name}`}
            aria-haspopup="true"
            aria-expanded={isOpen}
            onClick={handleToggleMenu}
            className={cx(classes.button, { [classes.isActive]: isOpen })}
            data-event={dataEvent}
            disableRipple
            role="button"
            data-testid={`mainHeaderSubMenuButton-${name}`}
          >
            <Typography variant="body" component="span">
              {content}
              <KeyboardArrowDown className={classes.buttonIcon} />
            </Typography>
          </Button>
          <Popper
            anchorEl={anchorEl.current}
            container={anchorEl.current}
            open={isOpen}
            className={classes.popper}
            placement="bottom-start"
            disablePortal
            keepMounted
            transition
            popperOptions={{
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    padding: 0,
                  },
                },
              ],
            }}
            role={undefined}
          >
            {({ TransitionProps }) => (
              <Collapse
                {...TransitionProps}
                in={isOpen}
                style={{ transitionTimingFunction: 'ease-in-out' }}
                timeout={transitionDuration}
              >
                <Paper
                  className={classes.dropdownRoot}
                  square
                  ref={menuParentRef}
                  data-testid={`mainHeaderSubMenu-${name}`}
                >
                  <MenuComponent name={name} onClick={closeMenu} />
                </Paper>
              </Collapse>
            )}
          </Popper>
        </div>
      </ClickAwayListener>
      <Backdrop
        style={{ top: `${refHeight}px` }}
        open={isOpen}
        className={classes.backdrop}
        transitionDuration={transitionDuration}
      />
    </>
  );
};

export default HeaderNavigationDropDown;
